import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import { getImageUrl } from '../utils/imageUtils';
import { showInquiryNotification } from '../utils/notificationUtils';
import { getStates, getCitiesForState } from '../utils/locationData';



const RentItems = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 12;
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState({
    listingType: '', // Add listing type filter
    itemType: '',
    category: '',
    clothingType: '',
    jewelleryType: '',
    handycraftType: '',
    size: '',
    jewellerySize: '',
    state: '',
    city: '',
    color: '',
    material: '',
    minPrice: '',
    maxPrice: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const filterRef = useRef(null);
  const debounceRef = useRef(null);

  // Image Carousel Component
  const SimpleImageDisplay = ({ images, itemName }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    if (!images || images.length === 0) {
      return (
        <div className="w-full h-[500px] rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <i className="fas fa-image text-4xl mb-2"></i>
            <p>No image available</p>
          </div>
        </div>
      );
    }

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToImage = (index) => {
      setCurrentImageIndex(index);
    };

    return (
      <div className="relative w-full h-[500px] rounded-lg overflow-hidden bg-gray-100 group">
        {/* Main Image */}
        <img
          src={getImageUrl(images[currentImageIndex])}
          alt={`${itemName} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-contain bg-gray-50 transition-opacity duration-300"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
          }}
        />

        {/* Navigation Arrows - Only show if more than 1 image */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 backdrop-blur-sm z-10"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 backdrop-blur-sm z-10"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm z-10">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Thumbnail Dots - Only show if more than 1 image */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToImage(index);
                }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const loadListings = async (page = currentPage, appliedFilters = {}, isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }

    try {
      // Clean up filters - remove empty values
      const cleanedFilters = {};
      Object.keys(appliedFilters).forEach(key => {
        if (appliedFilters[key] && appliedFilters[key] !== '') {
          cleanedFilters[key] = appliedFilters[key];
        }
      });

      const params = new URLSearchParams({
        page: page,
        limit: ITEMS_PER_PAGE,
        ...cleanedFilters
      });

      const response = await api.get(`/listings?${params}`);

      // Safety check for response structure
      if (response.data && Array.isArray(response.data.listings)) {
        setListings(response.data.listings);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
        setTotalItems(response.data.total || 0);
      } else {
        setListings([]);
        setTotalPages(1);
        setCurrentPage(1);
        setTotalItems(0);
      }
    } catch (error) {
      toast.error('Error loading listings');
      // Ensure state is reset to safe defaults on error
      setListings([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalItems(0);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setFilterLoading(false);
      }
    }
  };

  // Debounced filter function
  const debouncedLoadListings = useCallback((appliedFilters, page = 1) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      loadListings(page, appliedFilters, false);
    }, 300); // 300ms debounce
  }, []);

  useEffect(() => {
    loadListings(1, {}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentPage > 1) {
      loadListings(currentPage, filters, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const checkScroll = () => {
      if (filterRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = filterRef.current;
        const scrollHint = document.getElementById('filter-scroll-hint');

        if (scrollHeight > clientHeight) {
          filterRef.current.classList.add('has-scroll');
          if (scrollHint && scrollTop < 10) {
            scrollHint.classList.remove('hidden');
          }
        } else {
          filterRef.current.classList.remove('has-scroll');
          if (scrollHint) {
            scrollHint.classList.add('hidden');
          }
        }
      }
    };

    const handleScroll = () => {
      if (filterRef.current) {
        const scrollHint = document.getElementById('filter-scroll-hint');
        if (scrollHint && filterRef.current.scrollTop > 10) {
          scrollHint.classList.add('hidden');
        }
      }
    };

    checkScroll();
    if (filterRef.current) {
      filterRef.current.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('resize', checkScroll);

    return () => {
      if (filterRef.current) {
        filterRef.current.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // If itemType is changing, clear related filters
    let newFilters;
    if (name === 'itemType') {
      newFilters = {
        ...filters,
        [name]: value
      };

      // Clear type-specific filters when switching item types
      if (value === 'clothing') {
        newFilters.jewelleryType = '';
        newFilters.jewellerySize = '';
        newFilters.material = '';
      } else if (value === 'jewellery') {
        newFilters.clothingType = '';
        newFilters.size = '';
      }
    } else if (name === 'state') {
      // When state changes, clear city
      newFilters = {
        ...filters,
        [name]: value,
        city: '' // Clear city when state changes
      };
    } else {
      newFilters = {
        ...filters,
        [name]: value
      };
    }

    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    debouncedLoadListings(newFilters, 1);
  };

  const applyFilters = () => {
    setCurrentPage(1);

    // Clean up filters based on itemType
    const cleanedFilters = { ...filters };

    // If itemType is clothing, remove jewellery-specific filters
    if (cleanedFilters.itemType === 'clothing') {
      delete cleanedFilters.jewelleryType;
      delete cleanedFilters.jewellerySize;
      delete cleanedFilters.material;
    }

    // If itemType is jewellery, remove clothing-specific filters
    if (cleanedFilters.itemType === 'jewellery') {
      delete cleanedFilters.clothingType;
      delete cleanedFilters.size;
    }

    // Remove empty filters
    Object.keys(cleanedFilters).forEach(key => {
      if (cleanedFilters[key] === '' || cleanedFilters[key] === null || cleanedFilters[key] === undefined) {
        delete cleanedFilters[key];
      }
    });

    loadListings(1, cleanedFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      itemType: '',
      category: '',
      clothingType: '',
      jewelleryType: '',
      size: '',
      jewellerySize: '',
      location: '',
      color: '',
      material: '',
      minPrice: '',
      maxPrice: ''
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadListings(1, {}, false);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value && value !== '').length;
  };

  const clearFilters = () => {
    setFilters({
      listingType: '',
      itemType: '',
      category: '',
      clothingType: '',
      jewelleryType: '',
      size: '',
      jewellerySize: '',
      state: '',
      city: '',
      color: '',
      material: '',
      minPrice: '',
      maxPrice: ''
    });
    setCurrentPage(1);
    loadListings(1, {});
  };

  const showItemDetail = async (itemId) => {
    try {
      // Track view
      try {
        const viewResponse = await api.post('/listings/track-view', { listingId: itemId });
      } catch (viewError) {
      }

      const response = await api.get(`/listings/${itemId}`);
      setSelectedItem(response.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Error loading item details');
    }
  };

  const startChat = async (listingId) => {
    try {
      const response = await api.post('/chat/start', { listingId });
      if (response.data) {
        setShowModal(false); // Close item modal

        if (response.data.isExisting) {
          // Existing chat found, redirect to chat page
          toast.success('Chat found! Redirecting to chat page...');
          setTimeout(() => {
            navigate('/chat');
          }, 500);
        } else {
          // No existing chat, redirect to chat page with listing data for first message
          toast.success('Ready to start chat! Redirecting...');
          setTimeout(() => {
            navigate('/chat', {
              state: {
                newChatData: response.data.chatData,
                listingId: listingId
              }
            });
          }, 500);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error starting chat';

      if (errorMessage.includes('cannot chat with yourself')) {
        toast.error('You cannot chat about your own listing');
      } else if (error.response?.status === 401) {
        toast.error('Please login to start a chat');
      } else {
        toast.error(errorMessage);
      }
    }
  };



  const ItemCard = ({ item }) => {
    return (
      <div
        className="group bg-white rounded-2xl shadow-lg overflow-hidden card-hover cursor-pointer border border-gray-100 hover:border-indigo-200 transition-all duration-300"
        onClick={() => showItemDetail(item._id)}
      >
        <div className="relative overflow-hidden">
          <img
            src={getImageUrl(item.images[0])}
            alt={item.itemName}
            className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {/* Status Badge */}
          <div className="absolute top-4 right-4 z-10">
            <span className={`px-4 py-2 rounded-xl text-xs font-bold shadow-2xl backdrop-blur-md ${
              (item.listingType || 'rent') === 'sell'
                ? 'bg-green-500 text-white'
                : item.availabilityStatus === 'available' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-orange-500 text-white'
            }`}>
              <i className={`mr-1.5 ${
                (item.listingType || 'rent') === 'sell'
                  ? 'fas fa-tag'
                  : item.availabilityStatus === 'available' 
                    ? 'fas fa-check-circle'
                    : 'fas fa-calendar-times'
              }`}></i>
              {(item.listingType || 'rent') === 'sell'
                ? 'For Sale'
                : item.availabilityStatus === 'available' 
                  ? 'Available'
                  : 'Currently Rented'
              }
            </span>
          </div>

          {/* Views Badge */}
          <div className="absolute top-4 left-4 z-10">
            <span className="bg-black/70 text-white px-3 py-1.5 rounded-xl text-xs font-semibold backdrop-blur-md flex items-center gap-1.5">
              <i className="fas fa-eye"></i>
              <span>{item.views || 0}</span>
            </span>
          </div>

          {/* Rental Period Overlay */}
          {item.availabilityStatus === 'rented' && item.rentalPeriod && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent flex items-end z-10">
              <div className="w-full p-4">
                <div className="bg-orange-500 text-white px-4 py-3 rounded-xl shadow-2xl backdrop-blur-sm">
                  <div className="flex items-center justify-center mb-1.5">
                    <i className="fas fa-calendar-alt mr-2"></i>
                    <span className="font-bold text-sm">Currently Rented</span>
                  </div>
                  <div className="text-xs opacity-95 text-center">
                    {item.rentalPeriod.startDate} - {item.rentalPeriod.endDate}
                  </div>
                  <div className="text-xs font-semibold mt-1.5 text-center bg-white/20 rounded-lg py-1">
                    <i className="fas fa-clock mr-1"></i>
                    Available in {item.rentalPeriod.daysRemaining} days
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Price Tag */}
          <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-baseline gap-2">
              <span className="text-white text-3xl font-bold drop-shadow-lg">₹{item.price}</span>
              {(item.listingType || 'rent') === 'rent' && (
                <span className="text-white/90 text-sm font-medium">/day</span>
              )}
            </div>
          </div>
        </div>

        <div className="p-5">
          <h3 className="font-bold text-xl mb-3 text-gray-900 line-clamp-1 group-hover:text-indigo-600 transition-colors">
            {item.itemName}
          </h3>

          {/* Location */}
          <div className="flex items-center gap-2 mb-4 text-gray-600">
            <i className="fas fa-map-marker-alt text-red-500"></i>
            <span className="text-sm font-medium">{item.location}</span>
            <span className="ml-auto bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
              {item.itemType === 'jewellery' ? (item.jewellerySize || 'Free Size') : item.size}
            </span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border border-blue-200">
              <i className={`fas ${item.itemType === 'jewellery' ? 'fa-gem' : 'fa-tshirt'} mr-1.5`}></i>
              {item.itemType === 'jewellery' ? item.jewelleryType : item.clothingType}
            </span>
            <span className="bg-gradient-to-r from-green-50 to-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border border-green-200">
              <i className="fas fa-star mr-1.5"></i>
              {item.condition}
            </span>
            <span className="bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border border-purple-200">
              <i className="fas fa-venus-mars mr-1.5"></i>
              {item.category || 'Handycraft'}
            </span>
            {item.itemType === 'jewellery' && item.material && (
              <span className="bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border border-yellow-200">
                <i className="fas fa-certificate mr-1.5"></i>
                {item.material}
              </span>
            )}
          </div>

          {/* View Details */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <span className="text-gray-500 text-sm font-medium group-hover:text-indigo-600 transition-colors">
              <i className="fas fa-info-circle mr-2"></i>
              View Details
            </span>
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-600 transition-colors">
              <i className="fas fa-arrow-right text-indigo-600 group-hover:text-white text-sm transition-colors"></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ItemModal = () => {
    if (!selectedItem) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-3xl animate-scale-in">
          {/* Header */}
          <div className="relative p-6 text-white" style={{ backgroundColor: 'var(--sage-green)' }}>
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all hover:rotate-90 duration-300"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
            <h2 className="text-3xl font-bold pr-12">{selectedItem.itemName}</h2>
            <div className="flex items-center gap-3 mt-3">
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                (selectedItem.listingType || 'rent') === 'sell'
                  ? 'bg-green-500 text-white'
                  : selectedItem.availabilityStatus === 'available' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-orange-500 text-white'
              }`}>
                <i className={`mr-1.5 ${
                  (selectedItem.listingType || 'rent') === 'sell'
                    ? 'fas fa-tag'
                    : selectedItem.availabilityStatus === 'available' 
                      ? 'fas fa-check-circle' 
                      : 'fas fa-calendar-times'
                }`}></i>
                {(selectedItem.listingType || 'rent') === 'sell'
                  ? 'For Sale'
                  : selectedItem.availabilityStatus === 'available' 
                    ? 'Available' 
                    : 'Currently Rented'
                }
              </span>
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-white/20 backdrop-blur-sm">
                <i className="fas fa-eye mr-1.5"></i>{selectedItem.views || 0} views
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Image Section */}
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-50 to-gray-100">
                  <SimpleImageDisplay
                    images={selectedItem.images}
                    itemName={selectedItem.itemName}
                  />
                </div>
              </div>

              {/* Details Section */}
              <div className="space-y-6">
                {/* Price */}
                <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-indigo-600">
                      ₹{selectedItem.price}
                    </span>
                    <span className="text-gray-600 font-medium">
                      {(selectedItem.listingType || 'rent') === 'rent' ? 'per day' : ''}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                      (selectedItem.listingType || 'rent') === 'rent' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      <i className={`fas ${(selectedItem.listingType || 'rent') === 'rent' ? 'fa-handshake' : 'fa-tag'} mr-2`}></i>
                      {(selectedItem.listingType || 'rent') === 'rent' ? 'For Rent' : 'For Sale'}
                    </span>
                  </div>
                </div>

                {/* Rental Timeline */}
                {selectedItem.availabilityStatus === 'rented' && selectedItem.rentalPeriod && (
                  <div className="bg-orange-50 rounded-2xl p-5 border-l-4 border-orange-500">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                        <i className="fas fa-calendar-alt text-white"></i>
                      </div>
                      <span className="font-bold text-orange-900 text-lg">Currently Rented</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-white/50 rounded-lg p-3">
                        <span className="text-orange-700 font-medium block mb-1">Rental Period</span>
                        <div className="text-orange-900 font-semibold">
                          {selectedItem.rentalPeriod.startDate} - {selectedItem.rentalPeriod.endDate}
                        </div>
                      </div>
                      <div className="bg-white/50 rounded-lg p-3">
                        <span className="text-orange-700 font-medium block mb-1">Available In</span>
                        <div className="text-orange-900 font-bold text-xl">
                          {selectedItem.rentalPeriod.daysRemaining} days
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Item Details */}
                <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
                  <h4 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                    <i className="fas fa-info-circle text-indigo-600"></i>
                    Item Details
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-xs text-gray-500 block mb-1">Item Type</span>
                      <span className="font-semibold text-gray-800 capitalize">{selectedItem.itemType}</span>
                    </div>
                    
                    {(selectedItem.itemType === 'clothing' && selectedItem.size) && (
                      <div className="bg-white rounded-lg p-3">
                        <span className="text-xs text-gray-500 block mb-1">Size</span>
                        <span className="font-semibold text-gray-800">{selectedItem.size}</span>
                      </div>
                    )}
                    
                    {(selectedItem.itemType === 'jewellery' && selectedItem.jewellerySize) && (
                      <div className="bg-white rounded-lg p-3">
                        <span className="text-xs text-gray-500 block mb-1">Size</span>
                        <span className="font-semibold text-gray-800">{selectedItem.jewellerySize}</span>
                      </div>
                    )}
                    
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-xs text-gray-500 block mb-1">Color</span>
                      <span className="font-semibold text-gray-800 capitalize">{selectedItem.color}</span>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-xs text-gray-500 block mb-1">Category</span>
                      <span className="font-semibold text-gray-800 capitalize">{selectedItem.category || 'Handycraft'}</span>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-xs text-gray-500 block mb-1">Type</span>
                      <span className="font-semibold text-gray-800 capitalize">
                        {selectedItem.itemType === 'clothing' ? selectedItem.clothingType : selectedItem.jewelleryType}
                      </span>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-xs text-gray-500 block mb-1">Condition</span>
                      <span className="font-semibold text-gray-800 capitalize">{selectedItem.condition}</span>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3 col-span-2">
                      <span className="text-xs text-gray-500 block mb-1">
                        <i className="fas fa-map-marker-alt mr-1"></i>Location
                      </span>
                      <span className="font-semibold text-gray-800">{selectedItem.location}</span>
                    </div>
                    
                    {selectedItem.fabric && (
                      <div className="bg-white rounded-lg p-3">
                        <span className="text-xs text-gray-500 block mb-1">Fabric</span>
                        <span className="font-semibold text-gray-800 capitalize">{selectedItem.fabric}</span>
                      </div>
                    )}
                    
                    {selectedItem.material && (
                      <div className="bg-white rounded-lg p-3">
                        <span className="text-xs text-gray-500 block mb-1">Material</span>
                        <span className="font-semibold text-gray-800 capitalize">{selectedItem.material}</span>
                      </div>
                    )}
                    
                    {selectedItem.metalPurity && (
                      <div className="bg-white rounded-lg p-3">
                        <span className="text-xs text-gray-500 block mb-1">Metal Purity</span>
                        <span className="font-semibold text-gray-800">{selectedItem.metalPurity}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white rounded-2xl p-5 border border-gray-200">
                  <h4 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                    <i className="fas fa-align-left text-indigo-600"></i>
                    Description
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{selectedItem.description}</p>
                </div>

                {/* Action Button */}
                {selectedItem.owner && selectedItem.owner._id !== user?._id ? (
                  <button
                    onClick={() => startChat(selectedItem._id)}
                    className="w-full bg-green-600 text-white py-4 rounded-xl hover:bg-green-700 font-bold text-lg transition-all transform hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-comments text-xl"></i>
                    Start Chat with Owner
                  </button>
                ) : selectedItem.owner && selectedItem.owner._id === user?._id ? (
                  <div className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl text-center font-bold text-lg flex items-center justify-center gap-2 border-2 border-gray-300">
                    <i className="fas fa-user text-xl"></i>
                    This is your listing
                  </div>
                ) : (
                  <div className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl text-center font-bold text-lg flex items-center justify-center gap-2 border-2 border-gray-300">
                    <i className="fas fa-sign-in-alt text-xl"></i>
                    Please login to chat
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };



  return (
    <>
      <Helmet>
        <title>Browse & Rent Designer Fashion, Jewellery & Handcrafts | VestrioZ Marketplace</title>
        <meta
          name="description"
          content="Browse 1000+ premium fashion items, designer jewellery & handcrafts for rent. Verified sellers, affordable prices, pan-India delivery. Find ethnic wear, party dresses, wedding outfits & more!"
        />
        <meta name="keywords" content="browse fashion items, rent designer clothes, rent jewellery online, handcraft rental, ethnic wear rental, party dress rental, wedding outfit rental, designer clothing marketplace, VestrioZ" />
        <link rel="canonical" href="https://vestrioz.com/rent-items" />
        <meta property="og:title" content="Browse & Rent Designer Fashion, Jewellery & Handcrafts" />
        <meta property="og:description" content="Browse 1000+ premium fashion items for rent. Verified sellers, affordable prices, pan-India delivery." />
        <meta property="og:url" content="https://vestrioz.com/rent-items" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#F6F1EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#222831' }}>
              Browse Items
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#393E46' }}>
              Discover amazing outfits and jewellery from our curated collection. Rent or buy the perfect style for any occasion.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="w-full btn-primary flex items-center justify-center"
              >
                <i className="fas fa-filter mr-2"></i>
                {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>

            {/* Filters Sidebar */}
            <div className={`lg:w-1/4 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
              <div ref={filterRef} className="backdrop-blur-sm rounded-2xl shadow-xl p-6 sticky top-4 border max-h-[calc(100vh-120px)] overflow-y-auto filter-scroll" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="flex items-center mb-6">
                  <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-filter text-white text-sm"></i>
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#222831' }}>Filters</h3>
                </div>

                {/* Listing Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: filters.listingType ? '#222831' : '#6B7280' }}>
                    Listing Type
                    {filters.listingType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                  </label>
                  <select
                    name="listingType"
                    value={filters.listingType}
                    onChange={handleFilterChange}
                    className={`w-full border rounded-md px-3 py-2 transition-colors ${filters.listingType
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <option value="">All (Rent & Sell)</option>
                    <option value="rent">For Rent</option>
                    <option value="sell">For Sale</option>
                  </select>
                </div>

                {/* Item Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: filters.itemType ? '#222831' : '#6B7280' }}>
                    Item Type
                    {filters.itemType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                  </label>
                  <select
                    name="itemType"
                    value={filters.itemType}
                    onChange={handleFilterChange}
                    className={`w-full border rounded-md px-3 py-2 transition-colors ${filters.itemType
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <option value="">All Items</option>
                    <option value="clothing">Clothing</option>
                    <option value="jewellery">Jewellery</option>
                    <option value="handycraft">Handy Craft</option>
                  </select>
                </div>

                {/* Category */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: filters.category ? '#222831' : '#6B7280' }}>
                    Category
                    {filters.category && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                  </label>
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className={`w-full border rounded-md px-3 py-2 transition-colors ${filters.category
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <option value="">All Categories</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>

                {/* Clothing Type - Only show for clothing or all items */}
                {(!filters.itemType || filters.itemType === 'clothing') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2" style={{ color: filters.clothingType ? '#222831' : '#6B7280' }}>
                      Clothing Type
                      {filters.clothingType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                      {filters.itemType === '' && <span className="text-xs text-gray-500 ml-1">(Clothing only)</span>}
                    </label>
                    <select
                      name="clothingType"
                      value={filters.clothingType}
                      onChange={handleFilterChange}
                      className={`w-full border rounded-md px-3 py-2 transition-colors ${filters.clothingType
                        ? 'border-green-400 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}
                      disabled={filters.itemType === 'jewellery'}
                    >
                      <option value="">All Types</option>
                      <option value="ethnic">Ethnic</option>
                      <option value="western">Western</option>
                      <option value="casual">Casual</option>
                      <option value="formal">Formal</option>
                      <option value="party">Party</option>
                      <option value="traditional">Traditional</option>
                    </select>
                  </div>
                )}

                {/* Jewellery Type - Only show for jewellery or all items */}
                {(!filters.itemType || filters.itemType === 'jewellery') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jewellery Type
                      {filters.itemType === '' && <span className="text-xs text-gray-500 ml-1">(Jewellery only)</span>}
                    </label>
                    <select
                      name="jewelleryType"
                      value={filters.jewelleryType}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={filters.itemType === 'clothing'}
                    >
                      <option value="">All Types</option>
                      <option value="necklace">Necklace</option>
                      <option value="earrings">Earrings</option>
                      <option value="bracelet">Bracelet</option>
                      <option value="ring">Ring</option>
                      <option value="anklet">Anklet</option>
                      <option value="bangles">Bangles</option>
                      <option value="chain">Chain</option>
                      <option value="pendant">Pendant</option>
                      <option value="brooch">Brooch</option>
                      <option value="cufflinks">Cufflinks</option>
                      <option value="watch">Watch</option>
                    </select>
                  </div>
                )}

                {/* Size - Only show for clothing or all items */}
                {(!filters.itemType || filters.itemType === 'clothing') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clothing Size
                      {filters.itemType === '' && <span className="text-xs text-gray-500 ml-1">(Clothing only)</span>}
                    </label>
                    <select
                      name="size"
                      value={filters.size}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={filters.itemType === 'jewellery'}
                    >
                      <option value="">All Sizes</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="XXL">XXL</option>
                      <option value="28">28</option>
                      <option value="30">30</option>
                      <option value="32">32</option>
                      <option value="34">34</option>
                      <option value="36">36</option>
                      <option value="38">38</option>
                      <option value="40">40</option>
                      <option value="42">42</option>
                    </select>
                  </div>
                )}

                {/* Jewellery Size - Only show for jewellery or all items */}
                {(!filters.itemType || filters.itemType === 'jewellery') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jewellery Size
                      {filters.itemType === '' && <span className="text-xs text-gray-500 ml-1">(Jewellery only)</span>}
                    </label>
                    <select
                      name="jewellerySize"
                      value={filters.jewellerySize}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={filters.itemType === 'clothing'}
                    >
                      <option value="">All Sizes</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="Free Size">Free Size</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                    </select>
                  </div>
                )}

                {/* Material - Only show for jewellery or all items */}
                {(!filters.itemType || filters.itemType === 'jewellery') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material
                      {filters.itemType === '' && <span className="text-xs text-gray-500 ml-1">(Jewellery only)</span>}
                    </label>
                    <select
                      name="material"
                      value={filters.material}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={filters.itemType === 'clothing'}
                    >
                      <option value="">All Materials</option>
                      <option value="gold">Gold</option>
                      <option value="silver">Silver</option>
                      <option value="platinum">Platinum</option>
                      <option value="diamond">Diamond</option>
                      <option value="pearl">Pearl</option>
                      <option value="gemstone">Gemstone</option>
                      <option value="artificial">Artificial</option>
                      <option value="brass">Brass</option>
                      <option value="copper">Copper</option>
                      <option value="stainless-steel">Stainless Steel</option>
                    </select>
                  </div>
                )}

                {/* Location - State and City */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <select
                    name="state"
                    value={filters.state}
                    onChange={handleFilterChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Select State</option>
                    {getStates().map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                {filters.state && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <select
                      name="city"
                      value={filters.city}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select City</option>
                      {getCitiesForState(filters.state).map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Price Range */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      placeholder="Min"
                      className="w-1/2 border border-gray-300 rounded-md px-3 py-2"
                    />
                    <input
                      type="number"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      placeholder="Max"
                      className="w-1/2 border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* Color */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <input
                    type="text"
                    name="color"
                    value={filters.color}
                    onChange={handleFilterChange}
                    placeholder="Enter color"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* Active Filters Display */}
                {Object.values(filters).some(value => value !== '') && (
                  <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(148, 137, 121, 0.1)' }}>
                    <h4 className="text-sm font-medium mb-2" style={{ color: '#393E46' }}>Active Filters:</h4>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(filters).map(([key, value]) => {
                        if (value === '') return null;
                        return (
                          <span key={key} className="text-xs px-2 py-1 rounded-full" style={{ background: '#948979', color: 'white' }}>
                            {key}: {value}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Filter Status and Actions */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {getActiveFilterCount() > 0 && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#948979' }}>
                          {getActiveFilterCount()} active
                        </span>
                      )}
                      {filterLoading && (
                        <div className="flex items-center">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-2 rounded-full animate-spin" style={{ borderTopColor: '#948979' }}></div>
                          <span className="ml-1 text-xs text-gray-500">Filtering...</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getActiveFilterCount() > 0 && (
                        <button
                          onClick={clearAllFilters}
                          className="text-xs px-3 py-1 rounded-full border transition-colors hover:bg-gray-50"
                          style={{ borderColor: '#948979', color: '#948979' }}
                        >
                          <i className="fas fa-times mr-1"></i>Clear All
                        </button>
                      )}
                      <div className="text-xs text-gray-500 hidden" id="filter-scroll-hint">
                        <i className="fas fa-arrow-down mr-1"></i>Scroll for more
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                      <i className="fas fa-magic mr-1"></i>
                      Filters apply automatically as you select options
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Grid */}
            <div className="lg:w-3/4">
              {/* Results Counter */}
              {!loading && (
                <div className="flex items-center justify-between mb-6">
                  <div className="text-sm" style={{ color: '#393E46' }}>
                    {Array.isArray(listings) && listings.length > 0 ? (
                      <>
                        Showing <span className="font-semibold">{Array.isArray(listings) ? listings.length : 0}</span> of <span className="font-semibold">{totalItems}</span> items
                        {getActiveFilterCount() > 0 && (
                          <span className="ml-1">
                            with <span className="font-semibold">{getActiveFilterCount()}</span> filter{getActiveFilterCount() > 1 ? 's' : ''} applied
                          </span>
                        )}
                      </>
                    ) : (
                      'No items found'
                    )}
                  </div>
                  {Array.isArray(listings) && listings.length > 0 && (
                    <div className="text-xs text-gray-500">
                      <i className="fas fa-info-circle mr-1"></i>
                      Page {currentPage} of {totalPages}
                    </div>
                  )}
                </div>
              )}

              {loading ? (
                <div className="text-center py-16">
                  <div className="relative">
                    <div className="w-20 h-20 mx-auto mb-6">
                      <div className="absolute inset-0 rounded-full border-4 border-indigo-200"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">Loading amazing items...</h3>
                    <p className="text-gray-500">Finding the perfect outfits for you</p>
                  </div>
                </div>
              ) : (!Array.isArray(listings) || listings.length === 0) ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                    <i className="fas fa-search text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-4">No items found</h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    We couldn't find any items matching your criteria. Try adjusting your filters or check back later for new arrivals.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="btn-primary"
                  >
                    <i className="fas fa-refresh mr-2"></i>Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="responsive-grid">
                    {listings.map(item => (
                      <ItemCard key={item._id} item={item} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-8 space-x-2">
                      {currentPage > 1 && (
                        <button
                          onClick={() => loadListings(currentPage - 1, filters)}
                          className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Previous
                        </button>
                      )}

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = Math.max(1, currentPage - 2) + i;
                        if (page > totalPages) return null;

                        return (
                          <button
                            key={page}
                            onClick={() => loadListings(page, filters)}
                            className={`px-3 py-2 border border-gray-300 rounded-md ${page === currentPage
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      {currentPage < totalPages && (
                        <button
                          onClick={() => loadListings(currentPage + 1, filters)}
                          className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Next
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Item Detail Modal */}
        {showModal && <ItemModal />}
        <Footer />
      </div>
    </>
  );
};

export default RentItems;